/**
 * Módulo que gerencia o estado das mensagens exibidas na aplicacao
 * @state Estados que devem ser controlados
 * @mutations Funções que tem permissão para alterar o state
 * @actions Executar operações asincronas e chamar mutations
 * @getters Retorna o estado além da possibilidade de retornar a
 * mesma lógica do computed (Gera valores com base em valores originais)
 */

import * as turf from '@turf/turf'

const initState = () => {
	return {
		/**
		 * Estado para armazenas o geojson
		 */
		geoJsonFzda: {},
		geoJsonTlho: {},
		geoJsonPtio: {},
		geoJsonPrcla: {},
		geoJsonBufferTalhao: {},
		geoJsonBufferParcela: {},

		bounds: {},

		/**
		 * Geojson Temporário para caso cancele importação de poligono
		 */
		tempGeoJson: [],

		/**
		 * Estados que identifica se Fazenda, Talhão ou Plantio possuem shape cadastrado no
		 * banco de dados da aplicacao
		 */
		fzdaHasFileInDB: false,
		prclaHasFileInDB: false,
		tlhoHasFileInDB: false,
		ptioHasFileInDB: false,
	}
}

const state = initState()

const mutations = {
	resetState(state) {
		Object.assign(state, initState())
	},
	/**
	 * Seta o valor para o bounds
	 */
	setBounds: (state, bbox) => {
		let coords = ['west', 'south', 'east', 'north']

		state.bounds = Object.fromEntries(
			bbox.map((m, i) => {
				return [coords[i], m]
			}),
		)
	},
	/**
	 * Seta o geojson de Fazenda, Talhão e Plantio
	 */
	setGeoJsonFzda: (state, value) => (state.geoJsonFzda = value),
	setGeoJsonTlho: (state, value) => (state.geoJsonTlho = value),
	setGeoJsonPtio: (state, value) => (state.geoJsonPtio = value),
	setGeoJsonPrcla: (state, value) => (state.geoJsonPrcla = value),
	setGeoJsonBufferTalhao: (state, value) => (state.geoJsonBufferTalhao = value),
	setGeoJsonBufferParcela: (state, value) => (state.geoJsonBufferParcela = value),

	/**
	 * Seta o geojson temporário
	 */
	setTempGeoJson: (state, value) => (state.tempGeoJson = value),

	/**
	 * Seta se possuem ou não shape salvo no banco de dados da aplicação
	 */
	setFzdaHasFileInDB: (state, value) => (state.fzdaHasFileInDB = value),
	setTlhoHasFileInDB: (state, value) => (state.tlhoHasFileInDB = value),
	setPtioHasFileInDB: (state, value) => (state.ptioHasFileInDB = value),
	setPrclaHasFileInDB: (state, value) => (state.prclaHasFileInDB = value),

	/**
	 * Reseta o estado para o valor inicial
	 */
	// resetImportShapeState: (state) => {
	//   state.geoJsonFzda = {};
	//   state.geoJsonTlho = [];
	//   state.geoJsonPtio = [];
	//   state.tempGeoJson = [];
	//   state.geoJsonBufferTalhao = [];
	//   state.geoJsonPrcla = [];
	//   state.fzdaHasFileInDB = false;
	//   state.tlhoHasFileInDB = false;
	//   state.ptioHasFileInDB = false;
	//   state.prclaHasFileInDB = false;
	//   state.geoJsonBufferParcela = {};
	//   state.geoJsonBufferTalhao = {};
	// },
}

const actions = {
	setAllShapes: ({ commit }, { fazenda, talhoes, plantios }) => {
		console.log('opa!')

		if (fazenda.geometry) {
			commit('setGeoJsonFzda', {
				coordinates: fazenda.geometry.coordinates,
				type: fazenda.geometry.type,
				properties: fazenda.properties,
			})

			commit(
				'setBounds',
				turf.bbox({
					coordinates: fazenda.geometry.coordinates,
					type: fazenda.geometry.type,
					properties: fazenda.properties,
				}),
			)

			commit('setFzdaHasFileInDB', true)
		}

		if (talhoes.features.some((feature) => feature.geometry)) {
			commit('setGeoJsonTlho', {
				features: talhoes.features.map((feature) => {
					return {
						coordinates: feature.geometry.coordinates,
						geometry: feature.geometry,
						properties: feature.properties,
						type: feature.geometry.type,
					}
				}),
				geojson: talhoes,
				properties: {},
			})

			commit('setTlhoHasFileInDB', true)
		}

		if (plantios.features.some((feature) => feature.geometry)) {
			commit('setGeoJsonPtio', {
				features: plantios.features.map((feature) => {
					return {
						coordinates: feature.geometry.coordinates,
						geometry: feature.geometry,
						properties: feature.properties,
						type: feature.geometry.type,
					}
				}),
				geojson: plantios,
				properties: {},
			})

			commit('setPtioHasFileInDB', true)
		}
	},
}

const getters = {
	/**
	 * @param {Object} state estado atual do vuex
	 * @returns lista de markers formatados para cards de info de plantio no mapa
	 */
	plantioMarkers: (state) =>
		Object.entries(state.geoJsonPtio).length
			? state.geoJsonPtio.features.map((feature) => {
					const { properties } = feature
					return {
						estratoFlorestalContinuo: properties.estratoFlorestalContinuo,
						estratosFlorestaisIPC: properties.estratosFlorestaisIPC,
						area: properties.Area,
						areaDoTalhao: properties.areaTalhao,
						codigo: properties['Codigo Plantio'],
						codigoTalhao: properties['Codigo Talhao'] || properties['Codigo do Talhao'],
						dataDoProcessamento: new Date().toDateString(),
						idEstratoInventarioFlorestal: 'NaN',
						idade: (
							(new Date() - new Date(properties['Data de Plantio'])) /
							(1000 * 3600 * 24) /
							365.5
						).toFixed(2),
						estrato: properties.estratoFlorestalContinuo,
						infoPoligono: {
							centroId: turf.centroid(feature).geometry,
							poligono: feature,
							id: properties.idPlantio,
						},
						materialGenetico: properties['Material Genetico'],
						porcentagemDeAreaPlantadaNoTalhao: 100,
						porcentagemDeArvoresUteis: 100,
						espacamento: properties.Espacamento,
						customs:
							properties.customs?.map((custom) => custom.Value) ||
							properties.customsPlantio?.map((custom) => custom.Value),
					}
				})
			: [],
	/**
	 * @param {Object} state o atual state do vuex
	 * @returns a soma da area dos plantios que também representa a area do Estrato
	 */
	areaEstrato: (state) =>
		Object.entries(state.geoJsonPtio).length
			? state.geoJsonPtio.features
					.map((m) => m.properties.Area)
					.reduce((previous, next) => previous + next)
			: 0,
	/**
	 * Método para recuperar o geojson atual que está sendo importado
	 */
	getGeoJsonFzda: (state) => state.geoJsonFzda,
	getGeoJsonTlho: (state) => state.geoJsonTlho,
	getGeoJsonPtio: (state) => state.geoJsonPtio,
	getGeoJsonBufferTalhao: (state) => state.geoJsonBufferTalhao,
	getGeoJsonBufferParcela: (state) => state.geoJsonBufferParcela,
	getGeoJsonPrcla: (state) => state.geoJsonPrcla,

	isGeoJsonParcelaEmpty: (state) => Object.keys(state.geoJsonPrcla).length === 0,

	isGeoJsonTalhaoEmpty: (state) => Object.keys(state.geoJsonTlho).length === 0,
	isGeoJsonPlantioEmpty: (state) => Object.keys(state.geoJsonPtio).length === 0,
	isGeoJsonFazendaEmpty: (state) => Object.keys(state.geoJsonFzda).length === 0,
	isGeoJsonBufferTalhaoEmpty: (state) => Object.keys(state.geoJsonBufferTalhao).length === 0,

	/**
	 * Retorna o geojson temporário
	 */
	getTempGeojson: (state) => state.tempGeoJson,

	/**
	 * Métodos para recuperar se Fazenda Talhão ou Plantio possuem shape cadastrado
	 * no banco de dados da aplicacao
	 */
	getFzdaHasFileInDB: (state) => state.fzdaHasFileInDB,
	getTlhoHasFileInDB: (state) => state.tlhoHasFileInDB,
	getPtioHasFileInDB: (state) => state.ptioHasFileInDB,
	getPrclaHasFileInDB: (state) => state.prclaHasFileInDB,
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
}
